import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const FlightDetailsCard = ({ flight, seg, onSelectFlight }) => {
  const [activeTab, setActiveTab] = useState("flight");
  const [showFareModal, setShowFareModal] = useState(false);
  const [selectedFareDetails, setSelectedFareDetails] = useState(null);
  const [showDetailsv, setShowDetailsv] = useState(false);
  const [selectedFareDetailsv, setSelectedFareDetailsv] = useState(null);
  // const [chosenFlightDetails, setChosenFlightDetails] = useState(null);


  const [selectedLeg, setSelectedLeg] = useState(null); // "onward" or "return"

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const journeyTypeParam = searchParams.get("journey_type");
  // At the top of your component
  const [selectedOnward, setSelectedOnward] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);

  // Map numeric values to trip type
  let tripType = "oneway";
  if (journeyTypeParam === "2") tripType = "round trip";
  if (journeyTypeParam === "3") tripType = "multicity";

  // Normalise segments and compute total duration.
  // If flight.Segments is nested (array-of-arrays -> multiple legs),
  // use the user's chosen rule: first segment Duration + last segment AccumulatedDuration
  // (with safe fallbacks). Otherwise flatten and sum per-segment durations.
  const isNestedSegments =
    Array.isArray(flight?.Segments) && flight.Segments.some(Array.isArray);

  let segmentsFlat = [];
  let firstSegment = seg;
  let lastSegment = seg;
  let totalMinutes = 0;

  // If a sort pass earlier cached a meta duration, prefer it (keeps display consistent with sort)
  const cachedMetaDuration = flight?.__meta?.duration;
  if (typeof cachedMetaDuration === "number" && !isNaN(cachedMetaDuration)) {
    totalMinutes = cachedMetaDuration;
    // We still try to derive first/last segments for display (icons/times)
    if (isNestedSegments) {
      const lastLeg = flight.Segments[flight.Segments.length - 1] || [];
      segmentsFlat = Array.isArray(lastLeg) ? lastLeg.flat() : [];
      firstSegment = segmentsFlat[0] || seg;
      lastSegment = segmentsFlat[segmentsFlat.length - 1] || seg;
    } else {
      segmentsFlat = Array.isArray(flight?.Segments) ? flight.Segments.flat() : [];
      firstSegment = segmentsFlat[0] || seg;
      lastSegment = segmentsFlat[segmentsFlat.length - 1] || seg;
    }
  } else if (isNestedSegments) {
    const lastLeg = flight.Segments[flight.Segments.length - 1] || [];
    // Use the last leg's flattened segments for UI listing
    segmentsFlat = Array.isArray(lastLeg) ? lastLeg.flat() : [];
    firstSegment = segmentsFlat[0] || seg;
    lastSegment = segmentsFlat[segmentsFlat.length - 1] || seg;

    const firstDur = firstSegment?.Duration ?? firstSegment?.duration ?? 0;
    const lastAccum =
      lastSegment?.AccumulatedDuration ?? lastSegment?.Duration ?? lastSegment?.duration ?? 0;
    const combined = (Number(firstDur) || 0) + (Number(lastAccum) || 0);
    const summed = segmentsFlat.reduce(
      (acc, s) => acc + (s.Duration || s.duration || 0),
      0
    );

    // Prefer the combined rule, then fall back to lastAccum, then summed, then seg fields
    if (combined > 0) {
      totalMinutes = combined;
    } else if (lastAccum > 0) {
      totalMinutes = lastAccum;
    } else if (summed > 0) {
      totalMinutes = summed;
    } else {
      totalMinutes =
        seg?.AccumulatedDuration ?? seg?.Duration ?? seg?.duration ?? 0;
    }
  } else {
    segmentsFlat = Array.isArray(flight?.Segments) ? flight.Segments.flat() : [];
    firstSegment = segmentsFlat[0] || seg;
    lastSegment = segmentsFlat[segmentsFlat.length - 1] || seg;
    const summed = segmentsFlat.reduce(
      (acc, s) => acc + (s.Duration || s.duration || 0),
      0
    );
    totalMinutes =
      lastSegment?.AccumulatedDuration ??
      lastSegment?.Duration ??
      lastSegment?.duration ??
      seg?.AccumulatedDuration ??
      seg?.Duration ??
      seg?.duration ??
      summed ?? 0;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return (
    <div className="flights_card">
      <div className="card">
        {/* Airline */}
        <div className="logo_side">
          <div>
            <img
              src={`/airlinesimages/${firstSegment?.Airline?.AirlineCode}.gif`}
              alt={`${firstSegment?.Airline?.AirlineName || "Airline"} Logo`}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/airlinesimages/default.gif";
              }}
            />
          </div>
          <div className="carft_det">
            <p className="AirlineName">{firstSegment?.Airline?.AirlineName}</p>
            <p className="AirlineCode">
              {firstSegment?.Airline?.AirlineCode}{" "}
              {firstSegment?.Airline?.FlightNumber}
            </p>
            <p className="Craft"><b>Aircraft:</b> {firstSegment?.Craft}</p>
          </div>
        </div>

        {/* Origin */}
        <div>
          <p className="CityName">
            {firstSegment?.Origin?.Airport?.CityName} (
            {firstSegment?.Origin?.Airport?.AirportCode})
          </p>
          <p className="DepTime">
            {new Date(firstSegment?.Origin?.DepTime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </p>
          <p className="Depdate">
            {new Date(firstSegment?.Origin?.DepTime).toLocaleDateString("en-GB")}
          </p>
          {firstSegment?.Origin?.Airport?.Terminal && (
            <p className="Terminal">
              Terminal {firstSegment.Origin.Airport.Terminal}
            </p>
          )}
        </div>

        {/* Duration & Stops */}
        <div>
          <p className="Duration">
            {`${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
              2,
              "0"
            )}m`}
          </p>
          <p className="StopOver">
            {segmentsFlat.length > 1
              ? `${segmentsFlat.length - 1} Stop${segmentsFlat.length - 1 > 1 ? "s" : ""
              }`
              : "Non Stop"}
          </p>
          {segmentsFlat.some((seg) => seg.GroundTime > 0) && (
            <p className="StopOver">
              Layover:{" "}
              {segmentsFlat
                .filter((seg) => seg.GroundTime > 0)
                .map(
                  (seg) =>
                    `${Math.floor(seg.GroundTime / 60)}h ${seg.GroundTime % 60
                    }m`
                )
                .join(", ")}
            </p>
          )}
        </div>

        {/* Destination */}
        <div>
          <p className="CityName">
            {lastSegment?.Destination?.Airport?.CityName} (
            {lastSegment?.Destination?.Airport?.AirportCode})
          </p>
          <p className="DepTime">
            {new Date(lastSegment?.Destination?.ArrTime).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
          </p>
          <p className="Depdate">
            {new Date(lastSegment?.Destination?.ArrTime).toLocaleDateString(
              "en-GB"
            )}
          </p>
          {lastSegment?.Destination?.Airport?.Terminal && (
            <p className="Terminal">
              Terminal {lastSegment.Destination.Airport.Terminal}
            </p>
          )}
        </div>

        {/* Fare */}
        <div>
          <p className="fare">
            ₹{" "}
            {flight?.Fare?.PublishedFare
              ? Math.round(flight.Fare.PublishedFare).toLocaleString("en-IN")
              : "N/A"}
          </p>
          <p className="total">Total Fare</p>
          <p className="Refundable">
            {flight?.IsRefundable ? "Refundable" : "Non-Refundable"}
          </p>
        </div>

        {/* Select Fare Button */}
        <div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSelectedFareDetails(flight);
              setShowFareModal(true);
            }}
          >
            Select fare
          </button>
        </div>
      </div>

      {/* View Details Toggle */}
      <div className="tab_flight_data">
        <div className="view_details">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setShowDetailsv(!showDetailsv);
              setSelectedFareDetailsv(flight);
            }}
          >
            View Details{" "}
            {showDetailsv ? (
              <FaChevronUp className="ml-2" />
            ) : (
              <FaChevronDown className="ml-2" />
            )}
          </button>
        </div>
      </div>

      {/* Tabs Modal */}
      {showDetailsv && selectedFareDetailsv && (
        <div
          className="fare-modal-overlay"
          onClick={() => setShowDetailsv(false)}
        >
          <div
            className="fare-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowDetailsv(false)}
            >
              ×
            </button>
            <div className="flight_tabs_section">
              <div className="tab_headers">
                <button
                  onClick={() => setActiveTab("flight")}
                  className={activeTab === "flight" ? "active" : ""}
                >
                  Flight Details
                </button>
                <button
                  onClick={() => setActiveTab("baggage")}
                  className={activeTab === "baggage" ? "active" : ""}
                >
                  Baggage
                </button>
                <button
                  onClick={() => setActiveTab("cancellation")}
                  className={activeTab === "cancellation" ? "active" : ""}
                >
                  Cancellation
                </button>
                <button
                  onClick={() => setActiveTab("fare")}
                  className={activeTab === "fare" ? "active" : ""}
                >
                  Fare Summary
                </button>
              </div>

              <div className="tab_content">
                {/* Flight Details */}
               {activeTab === "flight" && (
                  <div>
                    {segmentsFlat.map((leg, idx) => (
                      <React.Fragment key={idx}>
                        <div className="card">
                          <div className="logo_side">
                            <div>
                              <img
                                className="popupflightlogo"
                                src={`/airlinesimages/${leg?.Airline?.AirlineCode}.gif`}
                                alt={`${leg?.Airline?.AirlineName || "Airline"} Logo`}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/airlinesimages/default.gif";
                                }}
                              />
                            </div>
                            <div className="carft_det">
                              <p className="AirlineName">
                                {leg?.Airline?.AirlineName}
                              </p>
                              <p className="AirlineCode">
                                {leg?.Airline?.AirlineCode}{" "}
                                {leg?.Airline?.FlightNumber}
                              </p>
                              <p className="Craft"><b>Aircraft:</b> {leg?.Craft}</p>
                            </div>
                          </div>

                          {/* Origin */}
                          <div>
                            <p className="CityName">
                              {leg?.Origin?.Airport?.CityName} (
                              {leg?.Origin?.Airport?.AirportCode})({leg?.Origin?.Airport?.AirportName})
                            </p>
                            <p className="DepTime">
                              {new Date(
                                leg?.Origin?.DepTime
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </p>
                            <p className="Depdate">
                              {new Date(
                                leg?.Origin?.DepTime
                              ).toLocaleDateString("en-GB")}
                            </p>
                            {leg?.Origin?.Airport?.Terminal && (
                              <p className="Terminal">
                                Terminal {leg.Origin.Airport.Terminal}
                              </p>
                            )}
                          </div>

                          {/* Duration */}
                          <div>
                            <p className="Duration">
                              {`${String(Math.floor(leg?.Duration / 60)).padStart(
                                2,
                                "0"
                              )}h ${String(leg?.Duration % 60).padStart(
                                2,
                                "0"
                              )}m`}
                            </p>
                            <p className="StopOver">
                              {leg?.StopOver === false ? "Non Stop" : "Stop"}
                            </p>
                          </div>

                          {/* Destination */}
                          <div>
                            <p className="CityName">
                              {leg?.Destination?.Airport?.CityName} (
                              {leg?.Destination?.Airport?.AirportCode}) ({leg?.Destination?.Airport?.AirportName})
                            </p>
                            <p className="DepTime">
                              {new Date(
                                leg?.Destination?.ArrTime
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </p>
                            <p className="Depdate">
                              {new Date(
                                leg?.Destination?.ArrTime
                              ).toLocaleDateString("en-GB")}
                            </p>
                            {leg?.Destination?.Airport?.Terminal && (
                              <p className="Terminal">
                                Terminal {leg.Destination.Airport.Terminal}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Layover Line */}
                        {idx < segmentsFlat.length - 1 &&
                          segmentsFlat[idx + 1]?.GroundTime > 0 && (() => {
                            const groundTime = segmentsFlat[idx + 1].GroundTime || 0;
                            const hours = Math.floor(groundTime / 60);
                            const minutes = groundTime % 60;

                            // Build display string dynamically
                            let display = "";
                            if (hours > 0) display += `${hours}h `;
                            if (minutes > 0) display += `${minutes}m`;

                            return (
                              <div className="layover-line">
                                <p className="StopOver">Layover: {display.trim()}</p>
                              </div>
                            );
                          })()}

                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Baggage */}
                {activeTab === "baggage" && (
                  <div>
                    <table className="baggage-table w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 border">Airline</th>
                          <th className="p-2 border">Sector</th>
                          <th className="p-2 border">Check-in Baggage</th>
                          <th className="p-2 border">Cabin Baggage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {segmentsFlat.map((leg, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 border flex items-center gap-2">
                              <img
                                src={`/airlinesimages/${leg?.Airline?.AirlineCode}.gif`}
                                alt={`${leg?.Airline?.AirlineName || "Airline"} Logo`}
                                className="w-6 h-6"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/airlinesimages/default.gif";
                                }}
                              />
                              {leg?.Airline?.AirlineName || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {leg?.Origin?.Airport?.AirportCode || "N/A"} -{" "}
                              {leg?.Destination?.Airport?.AirportCode || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {leg?.Baggage || "N/A"}
                            </td>
                            <td className="p-2 border">
                              {leg?.CabinBaggage || "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Cancellation */}
                {activeTab === "cancellation" && (
                  <div>
                    <h4>Cancellation / Reissue Rules</h4>
                    {Array.isArray(flight?.MiniFareRules) &&
                      flight.MiniFareRules.length > 0 ? (
                      <div className="fare-table">
                        {flight.MiniFareRules.flat().map((rule, index) => {
                          const journeyFrom = (Number(rule?.From) || 0) + 1;

                          // ✅ Handle To value
                          let journeyTo;
                          if (!rule?.To || Number(rule.To) === 0) {
                            journeyTo = "Before 1 Day";
                          } else {
                            journeyTo = rule.To;
                          }

                          return (
                            <p key={index}>
                              {`${rule?.JourneyPoints || "-"} - ${journeyFrom} to ${journeyTo}: ${rule?.Unit || "-"} ${rule?.Type || "-"} — ${rule?.Details || "-"
                                }`}
                            </p>
                          );
                        })}
                      </div>

                    ) : (
                      <p>No cancellation or reissue rules available.</p>
                    )}
                  </div>
                )}

                {/* Fare */}
                {activeTab === "fare" && (
                  <div>
                    <h4>Fare Summary</h4>
                    {flight?.Fare ? (
                      <table className="fare-table">
                        <thead>
                          <tr>
                            <th>Passenger Type</th>
                            <th>Base Fare</th>
                            <th>Tax</th>
                            <th>Other Charges</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Adult</td>
                            <td>
                              ₹
                              {flight?.Fare?.BaseFare?.toLocaleString("en-IN") ||
                                0}
                            </td>
                            <td>
                              ₹
                              {flight?.Fare?.Tax?.toLocaleString("en-IN") || 0}
                            </td>
                            <td>
                              ₹
                              {flight?.Fare?.OtherCharges?.toLocaleString(
                                "en-IN"
                              ) || 0}
                            </td>
                            <td>
                              ₹
                              {Math.round(
                                (flight?.Fare?.BaseFare || 0) +
                                (flight?.Fare?.Tax || 0) +
                                (flight?.Fare?.OtherCharges || 0)
                              ).toLocaleString("en-IN")}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <p>No fare breakdown available.</p>
                    )}
                    <div className="fare-total" style={{ marginTop: "1rem" }}>
                      <strong>Total Fare:</strong> ₹
                      {flight?.Fare?.PublishedFare
                        ? Math.round(flight.Fare.PublishedFare).toLocaleString(
                          "en-IN"
                        )
                        : 0}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fare Modal */}
      {showFareModal && selectedFareDetails && (
        <div
          className="fare-modal-overlay"
          onClick={() => setShowFareModal(false)}
        >
          <div
            className="fare-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowFareModal(false)}
            >
              ×
            </button>
            {/* Flight summary */}
            <div className="flight-summary-bar">
              <div className="logo-section">
                <img
                  src={`/airlinesimages/${firstSegment?.Airline?.AirlineCode}.gif`}
                  alt={`${firstSegment?.Airline?.AirlineName || "Airline"} Logo`}
                  className="airline-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/airlinesimages/default.gif";
                  }}
                />
                <div className="airline-text">
                  <strong>{firstSegment?.Airline?.AirlineName}</strong>
                  <span className="route">
                    {firstSegment?.Origin?.Airport?.CityName} →{" "}
                    {lastSegment?.Destination?.Airport?.CityName}
                  </span>
                  <span className="timing">
                    {new Date(
                      firstSegment?.Origin?.DepTime
                    ).toLocaleDateString("en-GB", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      year: "2-digit",
                    })}{" "}
                    | Departure at{" "}
                    {new Date(firstSegment?.Origin?.DepTime).toLocaleTimeString(
                      [],
                      { hour: "2-digit", minute: "2-digit", hour12: false }
                    )}{" "}
                    | Arrival at{" "}
                    {new Date(
                      lastSegment?.Destination?.ArrTime
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Fare Cards */}
            <div className="fare-cards">
              {(selectedFareDetails?.FareOptions || [selectedFareDetails]).map(
                (fare, idx) => {
                  const segmentsFlat = fare?.Segments?.flat() || [];
                  return (
                    <div className="fare-card" key={idx}>
                      <div className="fare-price">
                        ₹
                        {fare?.Fare?.PublishedFare
                          ? Math.round(
                            fare.Fare.PublishedFare
                          ).toLocaleString("en-IN")
                          : "N/A"}
                        <span className="per-adult">Per Adult</span>
                      </div>
                     
                      <div className="fare-type">
                        {fare?.FareClassification?.Type || "Regular"}
                      </div>

                      <div className="fare-section">
                        <strong>Baggage</strong>
                        {segmentsFlat.map((seg, i) => (
                          <p key={`bag-${i}`}>
                            {seg?.Origin?.Airport?.CityName} →{" "}
                            {seg?.Destination?.Airport?.CityName}:{" "}
                            {seg?.CabinBaggage || "N/A"} Cabin /{" "}
                            {seg?.Baggage || "N/A"} Check-in
                          </p>
                        ))}
                      </div>

                      <div className="fare-section">
                        <strong>Seats & Meals</strong>
                        <p>
                          <b>Seat Charge:</b>{" "}
                          {fare?.Fare?.TotalSeatCharges || "N/A"}
                        </p>
                        <p>
                          <b>Meal Charge:</b>{" "}
                          {fare?.Fare?.TotalMealCharges || "N/A"}
                        </p>
                      </div>

                      <div className="fare-table">
                        <strong>Cancellation or Reissue</strong>
                        {Array.isArray(flight?.MiniFareRules) &&
                          flight.MiniFareRules.length > 0 ? (
                          flight.MiniFareRules.flat().map((rule, index) => {
                            const journeyFrom = (Number(rule?.From) || 0) + 1;
                            return (
                              <p key={index}>
                                {`${rule?.JourneyPoints ?? "0"}- ${journeyFrom} to ${rule?.To ?? "0"
                                  }: ${rule?.Unit ?? "0"} ${rule?.Type ?? "0"} — ${rule?.Details ?? "0"
                                  }`}
                              </p>
                            );
                          })
                        ) : (
                          <p>No cancellation or reissue rules available.</p>
                        )}
                      </div>
                    <button
  className="btn btn-primary"
  style={{ marginTop: "1rem" }}
  onClick={() => {
    if (tripType === "round trip") {
      // Round-trip selection (e.g., select onward flight)
      onSelectFlight(flight);
    } else {
      // One-way booking: navigate directly to Booking page
      navigate("/book-flight", {
        state: {
          selectedOnward: flight, // the selected flight
          selectedReturn: null,   // no return flight
          tripType: "oneway",
        },
      });
    }
  }}
>
  {tripType === "round trip" ? "Select" : "Book Now"}
</button>





                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}
      



    </div>

  );
};

export default FlightDetailsCard;
